import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from "src/environments/environment";
import { AppConstants } from "src/utils/app.constants";
import { AuthResponse, HandoffCodeResponse, HandoffPayload } from "./auth/auth-response.interface";
import { BehaviorSubject, from, Observable } from "rxjs";
import { UserProfile } from "./auth/userProfile";
import { LocalStorage } from "src/utils/local-storage";
import { catchError, map } from "rxjs/operators";
import { of } from 'rxjs';

@Injectable({providedIn: 'root'})
export class AuthService {
  
    public static userProfile: UserProfile;

    public ipCheckMessage$: BehaviorSubject<string>;

    constructor(private httpClient: HttpClient, private localStorage: LocalStorage) {
      this.ipCheckMessage$ = new BehaviorSubject<string>(null);
    }

    public checkToken(token: string): Observable<AuthResponse> {

      const url = environment.CPHA_AUTH_ENDPOINT + '/tokenCheck';
      return this.httpClient.post<AuthResponse>(url, {}, {headers: { Authorization: token }});
    }

    public createExchangeCode(): Observable<HandoffCodeResponse> {
      const url = `${environment.CPHA_AUTH_ENDPOINT}/handoff/code`;
      const accessToken = this.localStorage.getAccessToken();
      const userProfile = this.localStorage.getUserProfile();
      const body: HandoffPayload = { accessToken, userProfile: userProfile };

  
      return this.httpClient.post<HandoffCodeResponse>(url, body,{ headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      } });
    }

    public loginRequest(username: string, password: string, origin?: string): Observable<AuthResponse> {
        const headers = new HttpHeaders({
            'Content-Type': 'application/x-www-form-urlencoded',
            'Authorization': 'Basic ' + btoa(username + ':' + password)
        });
        let params = new HttpParams()
                          .set("grant_type", AppConstants.AUTH_SERVICE_PASSWORD_GRANT);
        if (origin) { 
           params = params.set("origin", origin);
        }
        const url = environment.CPHA_AUTH_ENDPOINT + "/oauth/token/" + AppConstants.AUTH_SERVICE_CLIENT_ID;
        return this.httpClient.post<AuthResponse>(url, null, {params:params, headers:headers});
    }

    getAccessTokenData(user: string, pass: string): Observable<AuthResponse> {
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': 'Basic ' + btoa(user+':'+pass)   
        });
        const params = new HttpParams()
                          .set("grant_type", AppConstants.AUTH_SERVICE_PASSWORD_GRANT);
        const url = environment.CPHA_AUTH_ENDPOINT + "/oauth/token/" + AppConstants.AUTH_SERVICE_CLIENT_ID;
        return this.httpClient.post<AuthResponse>(url, null, {params:params, headers:headers});
    }

    getAutoLoginByParam(pParam: string): Observable<AuthResponse> {
      return this.oauthTokenRequest(pParam, AppConstants.AUTH_SERVICE_AUTOLOGIN_GRANT);
    }

    private oauthTokenRequest(token: string, grantType: string): Observable<AuthResponse> {
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': 'Basic ' + token
      });

      let params = null;

      // if they are using autologin, they don't have a profile set so we need to send the
      // bare minimum so the backend can fetch that info
      if (grantType === AppConstants.AUTH_SERVICE_REFRESHTOKEN_GRANT) {
        params = new HttpParams()
          .set("grant_type", grantType).append("client", "hub").append("origin", "hub");
      }
      else if (grantType === AppConstants.AUTH_SERVICE_AUTOLOGIN_GRANT) {
        params = new HttpParams()
          .set("grant_type", grantType).append("client", "hub").append("origin", "hub");
      } else {

        //const activeSubCode = AuthService.userProfileState.userProfile.active_Subscription;

        // This has been modified to account for legacy subscription package types such as etp, et, etma etc.
        // These occurrences should rarely if ever happen now.
        // const activeSub = AuthService.userProfileState.userProfile.subscriptionInfo
        //   .filter(s => s.packageCode === activeSubCode || (s.packageCode !== 'etpc' && s.packageCode !== 'ecps'))[0];

        // params = new HttpParams()
        //   .set("grant_type", grantType)
        //   .append("uid", AuthService.userProfileState.userProfile.userId)
        //   .append("pkg", activeSubCode)
        //   .append("subs", activeSub.maxlicenses)
        //   .append("subid", AuthService.userProfileState.userProfile.subscriberId);
      }
        
      const url = environment.CPHA_AUTH_ENDPOINT + "/oauth/token/" + AppConstants.AUTH_SERVICE_CLIENT_ID;
      return this.httpClient.post<AuthResponse>(url, null, { params: params, headers: headers });
    }
    
    checkAutologinByIP(): Observable<any> {
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
        });
        const params = new HttpParams()
                            .set('origin', 'hub');      
        const url = environment.CPHA_AUTH_ENDPOINT + "/ipcheck";
        return this.httpClient.get(url, {params:params, headers:headers});
    }
    
    getpParam(token: string): Observable<any> {
        if (token != '') {
          const headers = new HttpHeaders({
              'Content-Type': 'application/text',
              'Authorization': 'Basic ' + token
          });
          const params = new HttpParams();
          const url = environment.CPHA_AUTH_ENDPOINT + "/pParam";
          return this.httpClient.post(url, null, {params:params, headers:headers});
        }
    }

    getRefreshToken(refreshToken: string): Observable<AuthResponse> {
        return this.oauthTokenRequest(refreshToken, AppConstants.AUTH_SERVICE_REFRESHTOKEN_GRANT);
    }

    logout(refreshToken: string, subscriberId: string): Observable<any> {
      if (!refreshToken || !subscriberId) {
        return from(Promise.resolve(null));
      }
    
      const headers = new HttpHeaders({
        'Content-Type': 'application/text',
        'Authorization': 'Basic ' + refreshToken
      });
    
      const params = new HttpParams().set('subid', subscriberId);    
      const url = environment.CPHA_AUTH_ENDPOINT + "/logout";    
      return this.httpClient.post(url, null, { params, headers });
    }
    
    setIpCheckMessage(message: string): void {
      this.ipCheckMessage$.next(message);
    }

    getIpCheckMessage(): BehaviorSubject<string> {
      return this.ipCheckMessage$;
    }

    public isAuthenticated(): Observable<boolean> {
      const accessToken = this.localStorage.getAccessToken();
      const userProfile = this.localStorage.getUserProfile();
    
      if (!accessToken || !userProfile) {
        return of(false);
      }
    
      const headerToken = accessToken.startsWith('Bearer ')
        ? accessToken
        : `Bearer ${accessToken}`;
    
      return this.checkToken(headerToken).pipe(
        map(() => {
          return true;
        }),
        catchError(err => {
          console.error(err);
          return of(false);
        })
      );
    }
}
